import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { format, subDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const AdvancedAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const { userProfile, loading: authLoading, signOut } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState(90);
  
  // Analytics data
  const [stats, setStats] = useState({
    totalReferrals: 0,
    totalPractitioners: 0,
    avgResponseTime: 0,
    completionRate: 0
  });
  const [trendsData, setTrendsData] = useState([]);
  const [practitionerData, setPractitionerData] = useState([]);
  const [urgencyData, setUrgencyData] = useState([]);

  // Check super admin access
  useEffect(() => {
    if (!authLoading && userProfile?.role !== 'super_admin') {
      navigate('/login', { replace: true });
    }
  }, [authLoading, userProfile, navigate]);

  // Load analytics data
  useEffect(() => {
    if (!authLoading && userProfile?.role === 'super_admin') {
      loadAnalytics();
    }
  }, [authLoading, userProfile, dateRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      
      const startDate = subDays(new Date(), dateRange);
      
      // Fetch all referrals
      const { data: referrals, error: refError } = await supabase?.from('referrals')?.select('*')?.gte('created_at', startDate?.toISOString())?.order('created_at', { ascending: true });
      
      if (refError) throw refError;
      
      // Fetch practitioners
      const { data: practitioners, error: practError } = await supabase?.from('practitioners')?.select('id, practitioner_name, email, status');
      
      if (practError) throw practError;
      
      // Calculate statistics
      const totalReferrals = referrals?.length || 0;
      const completedReferrals = referrals?.filter(r => r?.status === 'completed')?.length || 0;
      const completionRate = totalReferrals > 0 ? Math.round((completedReferrals / totalReferrals) * 100) : 0;
      
      // Calculate average response time
      let avgResponseTime = 0;
      const referralsWithTime = referrals?.filter(r => r?.updated_at && r?.created_at);
      if (referralsWithTime?.length > 0) {
        const totalTime = referralsWithTime?.reduce((sum, r) => {
          const diff = new Date(r.updated_at) - new Date(r.created_at);
          return sum + diff;
        }, 0);
        avgResponseTime = Math.round(totalTime / referralsWithTime?.length / (1000 * 60 * 60)); // hours
      }
      
      setStats({
        totalReferrals,
        totalPractitioners: practitioners?.filter(p => p?.status === 'active')?.length || 0,
        avgResponseTime,
        completionRate
      });
      
      // Process trends data (group by day)
      const trendsByDate = {};
      referrals?.forEach(ref => {
        const date = format(new Date(ref.created_at), 'dd/MM', { locale: fr });
        if (!trendsByDate?.[date]) {
          trendsByDate[date] = { date, total: 0, urgent: 0, normal: 0 };
        }
        trendsByDate[date].total++;
        if (ref?.urgency === 'urgent' || ref?.urgency === 'emergency') {
          trendsByDate[date].urgent++;
        } else {
          trendsByDate[date].normal++;
        }
      });
      setTrendsData(Object.values(trendsByDate));
      
      // Process practitioner performance
      const practPerformance = {};
      referrals?.forEach(ref => {
        const practId = ref?.practitioner_id;
        if (!practPerformance?.[practId]) {
          practPerformance[practId] = {
            id: practId,
            total: 0,
            pending: 0,
            completed: 0
          };
        }
        practPerformance[practId].total++;
        if (ref?.status === 'new') practPerformance[practId].pending++;
        if (ref?.status === 'completed') practPerformance[practId].completed++;
      });
      
      const practWithNames = Object.values(practPerformance)?.map(perf => {
        const pract = practitioners?.find(p => p?.id === perf?.id);
        return {
          ...perf,
          name: pract?.practitioner_name || 'Inconnu',
          email: pract?.email || ''
        };
      })?.sort((a, b) => b?.total - a?.total);
      
      setPractitionerData(practWithNames);
      
      // Process urgency distribution
      const urgencyCount = {};
      referrals?.forEach(ref => {
        const urgency = ref?.urgency || 'normal';
        urgencyCount[urgency] = (urgencyCount?.[urgency] || 0) + 1;
      });
      
      setUrgencyData([
        { name: 'Normal', value: urgencyCount?.normal || 0, color: '#60a5fa' },
        { name: 'Urgent', value: urgencyCount?.urgent || 0, color: '#fbbf24' },
        { name: 'Urgence', value: urgencyCount?.emergency || 0, color: '#ef4444' }
      ]);
      
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError('Erreur lors du chargement des analyses');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#0F0F12] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
          <p className="text-secondary">Chargement des analyses...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Advanced Analytics Dashboard - Rapports Avancés</title>
      </Helmet>
      <div className="min-h-screen bg-[#0F0F12]">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-xl border-b border-white/8 shadow-xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/super-admin-dashboard')}
                  className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                >
                  <Icon name="ArrowLeft" size={20} className="text-secondary" />
                </button>
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Icon name="BarChart3" size={24} className="text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-primary">Advanced Analytics Dashboard</h1>
                  <p className="text-sm text-muted">Rapports et analyses avancées</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <Button
                  onClick={() => loadAnalytics()}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Icon name="RefreshCw" size={18} />
                  <span className="hidden sm:inline">Actualiser</span>
                </Button>
                <div className="flex items-center gap-3 px-4 py-2 bg-card-elevated rounded-xl">
                  <div className="w-8 h-8 bg-gradient-to-br from-accent to-primary rounded-lg flex items-center justify-center">
                    <Icon name="User" size={16} className="text-white" />
                  </div>
                  <div className="hidden sm:block">
                    <p className="text-sm font-medium text-primary">{userProfile?.fullName}</p>
                    <p className="text-xs text-muted">Super Admin</p>
                  </div>
                </div>
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-2 px-4 py-2 text-error hover:bg-error-bg rounded-xl transition-colors"
                >
                  <Icon name="LogOut" size={20} />
                  <span className="hidden sm:inline">Déconnexion</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-error-bg border border-error/30 rounded-xl p-4 flex items-center gap-3 shadow-lg">
              <Icon name="AlertCircle" size={20} className="text-error" />
              <p className="text-sm text-secondary">{error}</p>
            </div>
          )}

          {/* Date Range Filter */}
          <div className="mb-6 bg-card rounded-xl p-4 border border-white/8">
            <div className="flex items-center gap-4">
              <Icon name="Calendar" size={20} className="text-muted" />
              <span className="text-sm text-secondary">Période:</span>
              <div className="flex gap-2">
                {[30, 60, 90, 180]?.map(days => (
                  <button
                    key={days}
                    onClick={() => setDateRange(days)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      dateRange === days
                        ? 'bg-accent text-white' :'bg-white/5 text-muted hover:bg-white/10'
                    }`}
                  >
                    {days} jours
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-card-elevated border border-white/8 p-6 rounded-xl hover:shadow-xl transition-all">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-blue-400/10 flex items-center justify-center shadow-lg">
                  <Icon name="FileText" size={24} className="text-blue-400" />
                </div>
              </div>
              <p className="text-3xl font-bold text-primary mb-1">{stats?.totalReferrals}</p>
              <p className="text-sm font-semibold text-secondary mb-1">Total Adressages</p>
              <p className="text-xs text-muted">Sur les {dateRange} derniers jours</p>
            </div>

            <div className="bg-card-elevated border border-white/8 p-6 rounded-xl hover:shadow-xl transition-all">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-green-400/10 flex items-center justify-center shadow-lg">
                  <Icon name="Users" size={24} className="text-green-400" />
                </div>
              </div>
              <p className="text-3xl font-bold text-primary mb-1">{stats?.totalPractitioners}</p>
              <p className="text-sm font-semibold text-secondary mb-1">Praticiens Actifs</p>
              <p className="text-xs text-muted">Avec au moins 1 adressage</p>
            </div>

            <div className="bg-card-elevated border border-white/8 p-6 rounded-xl hover:shadow-xl transition-all">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-purple-400/10 flex items-center justify-center shadow-lg">
                  <Icon name="Clock" size={24} className="text-purple-400" />
                </div>
              </div>
              <p className="text-3xl font-bold text-primary mb-1">{stats?.avgResponseTime}h</p>
              <p className="text-sm font-semibold text-secondary mb-1">Temps Moyen Réponse</p>
              <p className="text-xs text-muted">Délai de traitement</p>
            </div>

            <div className="bg-card-elevated border border-white/8 p-6 rounded-xl hover:shadow-xl transition-all">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-xl bg-green-400/10 flex items-center justify-center shadow-lg">
                  <Icon name="CheckCircle" size={24} className="text-green-400" />
                </div>
              </div>
              <p className="text-3xl font-bold text-primary mb-1">{stats?.completionRate}%</p>
              <p className="text-sm font-semibold text-secondary mb-1">Taux Complétion</p>
              <p className="text-xs text-muted">Cas traités avec succès</p>
            </div>
          </div>

          {/* Trends Chart */}
          <div className="bg-card rounded-2xl shadow-2xl border border-white/8 p-6 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-blue-400/10 flex items-center justify-center">
                <Icon name="TrendingUp" size={20} className="text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-primary">Tendances des Adressages</h3>
                <p className="text-sm text-muted">Évolution quotidienne des soumissions</p>
              </div>
            </div>

            {trendsData?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2e" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#6b7280"
                    style={{ fontSize: '12px' }}
                  />
                  <YAxis 
                    stroke="#6b7280"
                    style={{ fontSize: '12px' }}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'rgba(26, 26, 30, 0.95)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '12px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
                      color: '#e5e7eb'
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="total" 
                    stroke="#60a5fa" 
                    strokeWidth={3}
                    name="Total"
                    dot={{ fill: '#60a5fa', r: 5 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="urgent" 
                    stroke="#fbbf24" 
                    strokeWidth={2}
                    name="Urgent"
                    dot={{ fill: '#fbbf24', r: 4 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="normal" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    name="Normal"
                    dot={{ fill: '#10b981', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-muted">
                <p>Aucune donnée disponible pour cette période</p>
              </div>
            )}
          </div>

          {/* Practitioner Performance */}
          <div className="bg-card rounded-2xl shadow-2xl border border-white/8 p-6 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-green-400/10 flex items-center justify-center">
                <Icon name="Users" size={20} className="text-green-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-primary">Performance des Praticiens</h3>
                <p className="text-sm text-muted">Top 10 praticiens par volume</p>
              </div>
            </div>

            {practitionerData?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/8">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-secondary">Praticien</th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-secondary">Total</th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-secondary">En attente</th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-secondary">Complétés</th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-secondary">Taux</th>
                    </tr>
                  </thead>
                  <tbody>
                    {practitionerData?.slice(0, 10)?.map((pract, index) => (
                      <tr key={pract?.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-sm font-medium text-primary">{pract?.name}</p>
                            <p className="text-xs text-muted">{pract?.email}</p>
                          </div>
                        </td>
                        <td className="text-center py-3 px-4">
                          <span className="text-sm font-semibold text-primary">{pract?.total}</span>
                        </td>
                        <td className="text-center py-3 px-4">
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/10 text-yellow-400">
                            {pract?.pending}
                          </span>
                        </td>
                        <td className="text-center py-3 px-4">
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-400">
                            {pract?.completed}
                          </span>
                        </td>
                        <td className="text-center py-3 px-4">
                          <span className="text-sm font-medium text-primary">
                            {pract?.total > 0 ? Math.round((pract?.completed / pract?.total) * 100) : 0}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-12 text-center text-muted">
                <p>Aucune donnée de performance disponible</p>
              </div>
            )}
          </div>

          {/* Urgency Distribution */}
          <div className="bg-card rounded-2xl shadow-2xl border border-white/8 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-red-400/10 flex items-center justify-center">
                <Icon name="AlertCircle" size={20} className="text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-primary">Distribution par Urgence</h3>
                <p className="text-sm text-muted">Répartition des cas par niveau de priorité</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              {urgencyData?.map((item, index) => (
                <div key={index} className="bg-card-elevated rounded-xl p-4 border border-white/8">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item?.color }}></div>
                    <p className="text-sm font-medium text-secondary">{item?.name}</p>
                  </div>
                  <p className="text-2xl font-bold text-primary">{item?.value}</p>
                  <p className="text-xs text-muted mt-1">
                    {stats?.totalReferrals > 0 ? Math.round((item?.value / stats?.totalReferrals) * 100) : 0}% du total
                  </p>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default AdvancedAnalyticsDashboard;
